import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Flame, HeartHandshake, Loader2, Landmark, MoonStar, BedDouble, Sprout, UtensilsCrossed, Crown, Blocks } from "lucide-react";

const TIERS = [
  { amount: 25, label: "Flower Seva", desc: "Fresh marigold garlands for Baba and Hanuman-ji" },
  { amount: 51, label: "Bhandara Seva", desc: "1 Bhakti Brick + helps serve sanctified meals to devotees" },
  { amount: 108, label: "Sacred Number", desc: "Exactly 4 Bhakti Bricks — the sacred 108" },
  { amount: 251, label: "Sanctum Seva", desc: "9 Bhakti Bricks — builds the granite garbhagriha (sanctum)" },
  { amount: 501, label: "Bhandara Hall", desc: "18 Bhakti Bricks — raises the bhandara kitchen & dining hall where all are fed" },
  { amount: 1008, label: "37 Bhakti Bricks", desc: "Thirty-seven Bhakti Bricks in your name — a wall of love" },
  { amount: 5000, label: "Founding Member", desc: "185 Bhakti Bricks + your name on the Founders Wall" },
  { amount: 50000, label: "Pillar of the Dham", desc: "1,851 Bhakti Bricks — a granite pillar of the mandir carries your family's name" },
  { amount: 100000, label: "Das of the Dham", desc: "3,703 Bhakti Bricks — like Hanuman, the Ram-das: the humblest servant holds the highest place; your family's name at the sanctum entrance" },
];

const IMPACTS = [
  { icon: Landmark, title: "The Temple", text: "A permanent, expanded mandir for Maharaj-ji and Lord Hanuman — a granite sanctum open every day of the year." },
  { icon: MoonStar, title: "The Meditation Hall", text: "A dedicated hall for Osho meditations, kirtan, and silent sits — where devotion and silence meet." },
  { icon: BedDouble, title: "Devotee Accommodation", text: "Simple, peaceful rooms on campus so seekers can stay the night and soak in the village." },
  { icon: Sprout, title: "Gardens of Peace", text: "Meditation gardens under the wide Texas sky — a quiet refuge for the whole Texoma community." },
  { icon: UtensilsCrossed, title: "Kitchen & Dining Hall", text: "A full bhandara kitchen and dining area — so no one who comes to Baba's door ever leaves hungry." },
];

const FUNDS = [
  { id: "construction", icon: Landmark, title: "Ashram Construction", desc: "Build the temple, hall, rooms & gardens — $2M goal" },
  { id: "general", icon: HeartHandshake, title: "General Fund", desc: "Daily seva — aarti, flowers, bhandara & upkeep" },
];

const BRICK_PRICE = 27;

function honorTitle(cents) {
  if (cents >= 10000000) return "Das of the Dham";
  if (cents >= 5000000) return "Pillar of the Dham";
  return "Founding Member";
}

function customHonorHint(amount) {
  if (amount >= 100000) return "This gift makes you the Das of the Dham — the humblest servant, the highest honor";
  if (amount >= 50000) return "This gift makes you a Pillar of the Dham";
  if (amount >= 5000) return "This gift makes you a Founding Member — Founders Wall, digital & physical";
  return "Enter any amount close to your heart";
}

function formatMoney(cents) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(cents / 100);
}

export default function NewTempleAppeal() {
  const [fund, setFund] = useState("construction");
  const [frequency, setFrequency] = useState("once");
  const [selected, setSelected] = useState(108);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [dedication, setDedication] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ raised_cents: 0, donor_count: 0, goal_cents: 200000000, general_cents: 0, total_bricks: 0 });
  const [donors, setDonors] = useState([]);
  const [founders, setFounders] = useState([]);

  useEffect(() => {
    api.get("/donations/summary").then((r) => setSummary((s) => ({ ...s, ...r.data }))).catch(() => {});
    api.get("/donations/recent").then((r) => setDonors(r.data.donors || [])).catch(() => {});
    api.get("/donations/founders").then((r) => setFounders(r.data.founders || [])).catch(() => {});
  }, []);

  const isCustom = custom.trim() !== "";
  const customAmount = parseInt(custom, 10);
  const percent = Math.min(100, Math.round((summary.raised_cents / summary.goal_cents) * 100));
  const effectiveAmount = isCustom ? (isNaN(customAmount) ? 0 : customAmount) : selected;
  const brickCount = Math.floor(effectiveAmount / BRICK_PRICE);

  const donate = async () => {
    if (isCustom && (!customAmount || customAmount < 1 || customAmount > 999999)) {
      toast.error("Please enter a custom amount between $1 and $999,999");
      return;
    }
    setLoading(true);
    try {
      const prefix = fund === "general" ? "general" : "donation";
      const lookup_key = isCustom
        ? `${prefix}_custom_${frequency === "monthly" ? "monthly" : "once"}`
        : `${prefix}_${selected}${frequency === "monthly" ? "_monthly" : ""}`;
      const sevaLabel = isCustom ? "Custom Seva" : TIERS.find((t) => t.amount === selected)?.label;
      const res = await api.post("/payments/checkout", {
        lookup_key,
        quantity: isCustom ? customAmount : 1,
        origin_url: window.location.origin,
        donor_name: name,
        dedication,
        seva_label: sevaLabel,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start the donation. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section id="donate" data-testid="donate-section" className="py-20 sm:py-28 bg-[#1C1917] text-stone-200 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 75% 20%, #F59E0B 0%, transparent 45%), radial-gradient(circle at 15% 85%, #DC2626 0%, transparent 40%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div>
            <p className="eyebrow !text-amber-400 mb-4">09 · A Bigger Home for Baba</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-amber-100" data-testid="appeal-heading">
              Help Us Build the New Ashram in Denison
            </h2>
            <p className="mt-6 text-stone-300 leading-relaxed">
              Baba's family in Texas is growing. Today our mandir gathers in a modest space — beautiful,
              but no longer big enough to hold everyone who comes for darshan, aarti, and bhandara.
            </p>
            <p className="mt-4 text-stone-300 leading-relaxed">
              We are building a permanent, expanded spiritual home for Neem Karoli Baba and Lord Hanuman in
              Denison, Texas — a proper temple for daily darshan, a meditation hall where silence is shared,
              accommodation for devotees, gardens of peace, and a bhandara kitchen and dining hall that feeds all.
              Every brick is seva. Every dollar is prasad returned to God.
            </p>

            <div className="mt-10 rounded-2xl border border-amber-100/15 bg-stone-900/60 p-6">
              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="text-3xl font-display font-semibold text-amber-300" data-testid="donation-raised-amount">{formatMoney(summary.raised_cents)}</p>
                  <p className="text-xs text-stone-400 mt-1">raised of {formatMoney(summary.goal_cents)} Ashram construction goal</p>
                </div>
                <p className="text-sm text-stone-400 text-right" data-testid="donation-donor-count">
                  {summary.donor_count > 0 ? `${summary.donor_count} sevak${summary.donor_count === 1 ? "" : "s"}` : "Be our first sevak"}
                  {summary.total_bricks > 0 && (
                    <span className="block mt-0.5 text-amber-400/90 font-semibold" data-testid="bricks-laid-count">
                      {summary.total_bricks.toLocaleString()} Bhakti Brick{summary.total_bricks === 1 ? "" : "s"} laid
                    </span>
                  )}
                </p>
              </div>
              <div className="h-3 rounded-full bg-stone-800 overflow-hidden" data-testid="donation-progress-bar">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 transition-all duration-1000"
                  style={{ width: `${Math.max(percent, 1.5)}%` }} />
              </div>
              <p className="mt-2.5 text-[11px] text-stone-500 text-center">Every $27 lays one Bhakti Brick in your name — 4 bricks make the sacred 108</p>
            </div>

            <div className="mt-10 space-y-5">
              {IMPACTS.map((imp) => (
                <div key={imp.title} className="flex gap-4" data-testid={`impact-${imp.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="shrink-0 w-11 h-11 rounded-full bg-amber-500/10 border border-amber-400/20 flex items-center justify-center">
                    <imp.icon className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-100 text-sm">{imp.title}</h3>
                    <p className="text-sm text-stone-400 mt-0.5 leading-relaxed">{imp.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl border border-amber-400/20 bg-amber-500/5 p-6" data-testid="recognition-explainer">
              <div className="flex gap-4">
                <Crown className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-100 text-sm">How We Honor Our Donors</h3>
                  <p className="text-sm text-stone-400 mt-1.5 leading-relaxed">
                    Every construction gift lays <span className="text-amber-300 font-semibold">Bhakti Bricks</span> in
                    your name on this website — one brick for every $27 (four bricks make the sacred $108). Gifts of <span className="text-amber-300 font-semibold">$5,000+</span> make
                    you a <span className="text-amber-300 font-semibold">Founding Member</span>, <span className="text-amber-300 font-semibold">$50,000+</span> a <span className="text-amber-300 font-semibold">Pillar of the Dham</span>,
                    and <span className="text-amber-300 font-semibold">$100,000+</span> a <span className="text-amber-300 font-semibold">Das of the Dham</span> — honored
                    digitally here and on the physical Founders Wall at the Ashram.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="bg-[#FDFBF7] text-stone-800 rounded-3xl p-7 sm:p-9 shadow-2xl shadow-black/40 lg:sticky lg:top-28"
            data-testid="donation-widget"
          >
            <div className="flex items-center gap-2 mb-5">
              <Flame className="flame h-5 w-5 text-orange-600" />
              <h3 className="font-display text-xl text-amber-950">Offer Your Seva</h3>
            </div>

            <div className="grid grid-cols-2 gap-2.5 mb-5">
              {FUNDS.map((f) => (
                <button key={f.id} type="button"
                  data-testid={`fund-option-${f.id}`}
                  onClick={() => setFund(f.id)}
                  className={`rounded-2xl border p-3.5 text-left transition-all duration-300 ${
                    fund === f.id
                      ? "border-amber-600 bg-amber-600/10 shadow-md shadow-amber-900/10"
                      : "border-amber-900/15 bg-white hover:border-amber-600/50"
                  }`}>
                  <f.icon className={`h-5 w-5 mb-2 ${fund === f.id ? "text-amber-700" : "text-stone-400"}`} />
                  <span className="block text-sm font-semibold text-amber-950">{f.title}</span>
                  <span className="block text-[10px] text-stone-500 mt-0.5 leading-snug">{f.desc}</span>
                </button>
              ))}
            </div>
            {fund === "general" && summary.general_cents > 0 && (
              <p className="text-xs text-center text-stone-500 -mt-2 mb-4" data-testid="general-fund-total">
                General Fund seva received so far: <span className="font-semibold text-amber-800">{formatMoney(summary.general_cents)}</span>
              </p>
            )}

            <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-amber-100/70 border border-amber-900/10 mb-6">
              {[{ id: "once", label: "One-time" }, { id: "monthly", label: "Monthly Seva" }].map((f) => (
                <button key={f.id} type="button"
                  data-testid={`donation-frequency-${f.id}`}
                  onClick={() => setFrequency(f.id)}
                  className={`rounded-full py-2 text-sm font-semibold transition-all duration-300 ${
                    frequency === f.id ? "bg-gradient-to-r from-amber-600 to-orange-700 text-white shadow" : "text-stone-600 hover:text-amber-900"
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-2.5">
              {TIERS.map((t) => (
                <button key={t.amount} type="button"
                  data-testid={`donation-tier-button-${t.amount}`}
                  onClick={() => { setSelected(t.amount); setCustom(""); }}
                  className={`rounded-xl border px-2 py-3 text-center transition-all duration-300 ${
                    !isCustom && selected === t.amount
                      ? "border-amber-600 bg-amber-600/10 shadow-md shadow-amber-900/10 -translate-y-0.5"
                      : "border-amber-900/15 bg-white hover:border-amber-600/50"
                  }`}>
                  <span className="block font-display text-lg font-semibold text-amber-950">${t.amount.toLocaleString()}</span>
                  <span className="block text-[10px] uppercase tracking-wider text-stone-500 mt-0.5">{t.label}</span>
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-stone-500 text-center" data-testid="donation-tier-description">
              {isCustom
                ? (fund === "construction" ? customHonorHint(effectiveAmount) : "Enter any amount close to your heart")
                : TIERS.find((t) => t.amount === selected)?.desc}
            </p>
            {fund === "construction" && brickCount >= 1 && (
              <p className="mt-1.5 text-xs text-center font-semibold text-amber-700 flex items-center justify-center gap-1.5" data-testid="brick-count-label">
                <Blocks className="h-3.5 w-3.5" />
                = {brickCount.toLocaleString()} Bhakti Brick{brickCount === 1 ? "" : "s"} in your name
              </p>
            )}

            <div className="mt-5">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Custom amount (USD)</label>
              <div className="relative mt-1.5">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-semibold">$</span>
                <Input
                  data-testid="donation-amount-input"
                  type="number" min="1" max="999999" placeholder="Your own offering"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  className="pl-8 h-12 rounded-xl border-amber-900/15 bg-white focus-visible:ring-amber-600"
                />
              </div>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Name (optional)</label>
                <Input data-testid="donation-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="For the Wall of Grace" className="mt-1.5 h-11 rounded-xl border-amber-900/15 bg-white focus-visible:ring-amber-600" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Dedication (optional)</label>
                <Input data-testid="donation-dedication-input" value={dedication} onChange={(e) => setDedication(e.target.value)}
                  placeholder="In honor of…" className="mt-1.5 h-11 rounded-xl border-amber-900/15 bg-white focus-visible:ring-amber-600" />
              </div>
            </div>

            <Button
              data-testid="donation-submit-button"
              onClick={donate}
              disabled={loading}
              className="mt-6 w-full h-13 py-4 rounded-full bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white text-base font-semibold shadow-xl shadow-amber-900/25 hover:-translate-y-0.5 transition-all duration-300"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                <>
                  <HeartHandshake className="h-5 w-5 mr-2" />
                  Donate {isCustom && customAmount ? `$${customAmount.toLocaleString()}` : `$${selected.toLocaleString()}`}{frequency === "monthly" ? " / month" : ""} with Love
                </>
              )}
            </Button>
            <p className="mt-3 text-[11px] text-center text-stone-400">
              {fund === "construction"
                ? "Ashram Construction Fund · 1 Bhakti Brick per $27 · Founders Wall $5,000+ · Pillar of the Dham $50,000+ · Das of the Dham $100,000+"
                : "Ashram & Temple General Fund · supports daily seva and upkeep"}
            </p>
            <p className="mt-1 text-[11px] text-center text-stone-400">
              Secure payment by Stripe. You will receive an email receipt.
            </p>
          </motion.div>
        </div>

        {founders.length > 0 && (
          <div className="mt-16" data-testid="founders-wall">
            <p className="eyebrow !text-amber-400 mb-2">The Founders Wall</p>
            <p className="text-sm text-stone-400 mb-6 max-w-2xl">
              Founding Members ($5,000+), Pillars of the Dham ($50,000+) and the Das of the Dham circle ($100,000+) —
              honored here digitally, and soon on the physical Founders Wall at the Ashram.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {founders.map((f, i) => (
                <div key={i} data-testid={`founder-card-${i}`}
                  className="rounded-2xl border border-amber-400/25 bg-gradient-to-b from-amber-500/10 to-transparent p-6 text-center">
                  <Crown className="mx-auto h-5 w-5 text-amber-400 mb-3" />
                  <p className="font-display text-lg text-amber-100">{f.donor_name}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-500/80 mt-1">{honorTitle(f.amount)}</p>
                  <p className="text-sm text-stone-400 mt-2">{formatMoney(f.amount)}{f.dedication ? ` · "${f.dedication}"` : ""}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {donors.length > 0 && (
          <div className="mt-12" data-testid="donors-wall">
            <p className="eyebrow !text-amber-400 mb-6">Wall of Grace — Recent Sevaks</p>
            <div className="flex flex-wrap gap-3">
              {donors.map((d, i) => (
                <div key={i} className="rounded-full border border-amber-100/15 bg-stone-900/60 px-5 py-2.5 text-sm"
                  data-testid={`donor-chip-${i}`}>
                  <span className="text-amber-200 font-semibold">{d.donor_name}</span>
                  <span className="text-stone-400"> · {formatMoney(d.amount)}{d.recurring ? "/mo" : ""}</span>
                  {d.seva_label && <span className="text-rose-300/70"> · {d.seva_label}</span>}
                  {d.bricks >= 1 && <span className="text-amber-400/80"> · {d.bricks} Bhakti Brick{d.bricks === 1 ? "" : "s"}</span>}
                  {d.fund === "general" && <span className="text-rose-300/70"> · General Fund</span>}
                  {d.dedication && <span className="text-stone-500 italic"> — {d.dedication}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
